import { useEffect, useRef, useState } from 'react'

import { useReducedMotion } from './useReducedMotion'

interface Options {
  threshold?: number
  rootMargin?: string
}

/**
 * Flips `data-in` once an element scrolls into view, then disconnects.
 * Stagger lives in CSS via `transition-delay: calc(var(--i) * 80ms)`.
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>({
  threshold = 0.15,
  rootMargin = '0px 0px -10% 0px',
}: Options = {}) {
  const ref = useRef<T | null>(null)
  const reduced = useReducedMotion()
  const [inView, setInView] = useState(false)

  useEffect(() => {
    if (reduced) {
      setInView(true)
      return
    }

    const el = ref.current
    if (!el) return

    if (typeof IntersectionObserver === 'undefined') {
      setInView(true)
      return
    }

    let delivered = false

    const observer = new IntersectionObserver(
      (entries) => {
        delivered = true
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true)
            observer.disconnect()
          }
        }
      },
      { threshold, rootMargin },
    )

    observer.observe(el)

    // Fail-safe. The hidden state is `opacity: 0`, so an observer that never
    // delivers would leave the section invisible forever.
    const failsafe = window.setTimeout(() => {
      if (!delivered) {
        setInView(true)
        observer.disconnect()
      }
    }, 1500)

    return () => {
      window.clearTimeout(failsafe)
      observer.disconnect()
    }
  }, [reduced, threshold, rootMargin])

  return { ref, inView }
}
